import { UseMutateFunction } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  HostingSiteAddBody,
  HostingSiteAddV2200,
} from '@newfold/huapi-js/src/index.schemas';

import useAccount from '~/hooks/useAccount';
import { useBrandInfo } from '~/hooks/useBrandInfo';

import { AddSiteFormDataTypes } from '..';
// @ts-ignore
import wordpressIcon from '../../Sites/assets/placeholders/wordpress-icon.svg';
import { useAddSiteContext } from '../context/AddSiteContext';
import BtnGroup from './MultiStepCard/BtnGroup';
import CardHeader from './MultiStepCard/CardHeader';

type AddSiteOptionsPropOptions = {
  formData?: AddSiteFormDataTypes;
  setFormData?: (state: AddSiteFormDataTypes | any) => void;
  createWebsite?: UseMutateFunction<
    AxiosResponse<HostingSiteAddV2200, any>,
    AxiosError<unknown, any>,
    {
      hostingId: string;
      data: HostingSiteAddBody;
    },
    void
  >;
  isPending?: boolean;
};

const AddSiteOptionsWithCTB = ({
  formData = undefined,
  setFormData = () => {},
  createWebsite = () => {},
  isPending = false,
}: AddSiteOptionsPropOptions) => {
  const { optionSelected, setOptionSelected } = useAddSiteContext();
  const { id: hostingId } = useAccount();
  const navigate = useNavigate();
  const { t } = useTranslation(['addSites', 'common']);
  const { brandFromJWT: brand } = useBrandInfo();
  const isBHorHG =
    brand.toLowerCase() === 'bluehost' || brand.toLowerCase() === 'hostgator';
  const siteAddSelection = [
    {
      id: 1,
      title: t('addSites:addSiteOptions.wordpressOption'),
      description: t('addSites:addSiteOptions.wordpressDesc'),
      testId: 'AddSite',
      subtype: 'wordpress',
      cardLogo: wordpressIcon,
      canShow: true,
    },
    {
      id: 2,
      title: t('addSites:addSiteOptions.migrateSiteOption'),
      description: (
        <Trans
          i18nKey="addSites:addSiteOptions.migrateSiteOptionDesc"
          values={{
            brand: t('common:brand.' + brand.toLowerCase()),
          }}
        />
      ),
      testId: 'MigrateSite',
      subtype: 'MigrateSite',
      cardLogo: wordpressIcon,
      canShow: isBHorHG,
    },
    {
      id: 3,
      title: t('addSites:addSiteOptions.emptyEnvironmentOption'),
      description: t('addSites:addSiteOptions.emptyEnvironmentDesc'),
      subtype: 'generic',
      testId: 'generic',
      canShow: true,
    },
  ];

  const handleContinueBtn = async () => {
    if (optionSelected === 'MigrateSite') {
      const postData = {
        subtype: 'wordpress',
        ss_wp_mig: true,
      };
      setFormData({ ...formData, isMigrating: true });
      await createWebsite(
        { hostingId, data: postData },
        {
          onSuccess: () => navigate('transfer'),
        },
      );
    } else {
      setFormData({
        ...formData,
        subtype: optionSelected,
      });
      setOptionSelected(optionSelected);
      navigate('title');
    }
  };

  const siteAddSelectionList = siteAddSelection.filter((item) => item.canShow);

  const handleCancel = () => {
    // Relative to "websites" and "hosting" tabs (routes) in AM, we still need to navigate -1 as the previous route could be different.
    // Refer to `src/scenes/AddSetupSiteFlows/scenes/AddSite.js:82` for specifics on how to handle back button behavior
    navigate(-1);
  };

  return (
    <Stack spacing={2}>
      <CardHeader
        headerTitle={t('addSites:addSiteOptions.title')}
        hasBackArrow={false}
      />
      <Grid container>
        <Grid item xs={12} md={10}>
          <Stack spacing={2}>
            {siteAddSelectionList.map((optionBtn, id) => (
              <Fragment key={id}>
                {optionBtn?.id === 3 && (
                  <Typography align="center">{t('or')}</Typography>
                )}
                <Card
                  key={optionBtn.id}
                  variant={
                    optionBtn?.subtype === optionSelected
                      ? 'selected'
                      : 'outlined'
                  }
                  onClick={() => {
                    setOptionSelected(optionBtn?.subtype);
                    setFormData({ ...formData, subtype: optionBtn?.subtype });
                  }}
                  data-testid={optionBtn.testId}
                  sx={{
                    borderRadius: '8px',
                    maxWidth: '500px',
                    cursor: 'pointer',
                  }}
                >
                  <Stack
                    spacing={2}
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-start"
                    margin={2}
                  >
                    {optionBtn?.cardLogo && (
                      <CardMedia
                        component="img"
                        src={optionBtn.cardLogo}
                        alt={optionBtn.title}
                        style={{ width: '30px', height: '30px' }}
                      />
                    )}
                    <Stack
                      spacing={1}
                      direction="column"
                      alignItems="flex-start"
                      justifyContent="center"
                    >
                      <Typography variant="h3">{optionBtn.title}</Typography>
                      <Typography variant="body2">
                        {optionBtn.description}
                      </Typography>
                    </Stack>
                  </Stack>
                </Card>
              </Fragment>
            ))}
          </Stack>
        </Grid>
      </Grid>
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <BtnGroup
          type={formData?.subtype || optionSelected}
          onCancelHandler={() => handleCancel()}
          hasSkipBtn={false}
          hasCancelBtn
          hasFinishBtn={false}
          onClickHandler={handleContinueBtn}
          disabled={optionSelected ? false : true}
          loading={isPending}
          showInstallationModal={optionSelected !== 'MigrateSite'}
        />
      </Stack>
    </Stack>
  );
};

export default AddSiteOptionsWithCTB;
